<template>
  <div
    class="product-card-sawmill"
    :class="{
      [$class('loading')]: !product,
      'show-secondary-image': showSecondaryImage,
    }"
  >
    <a
      :href="product.url"
      class="product-card-sawmill__content"
    >
      <div class="product-card-sawmill__header">
        <h3
          class="
          product-card-sawmill__title
          text-heading-l
          text-heading-2xl-desktop
        "
          v-text="product.title"
        />
        <div
          v-if="hasBadge"
          class="main-product-card-sawmill__badges"
        >
         <product-badge
            v-if="sale"
            :text="$string('collection.badge.sale')"
            :badge-class="productBadgeClass($string('collection.badge.sale'))"
          />
          <product-badge
            v-else-if="badges && badges.length > 0"
            :text="productBadgeText(badges[0])"
            :badge-class="productBadgeClass(badges[0])"
          />
        </div>
      </div>

      <div
        v-if="product.navigationDescription"
        class="
          product-card-sawmill__description
          mt-m mt-l-desktop
          text-body-m-regular
          text-body-xl-regular-desktop"
        v-html="product.navigationDescription"
      />

      <product-prices
        class="product-card-sawmill__prices mt-m mt-xl-desktop"
        sawmill="product-card-sawmill__prices mt-m mt-xl-desktop"
        class-price="text-heading-s text-heading-l-desktop"
        class-compare-at-price="text-heading-s text-heading-l-desktop"
        class-varies="
          product-card-sawmill__price-varies text-body-m-regular text-body-l-regular-desktop
        "
        :product="product"
        :available="product?.available"
        :varies="product?.price?.varies"
        :compare-at-price="product?.compareAtPrice?.amount"
        :price="product?.price?.amount"
        :start-price="product?.price?.min"
        :affirm = true
      />

      <div
        v-if="!isMobile"
        class="product-card-sawmill__mobile-action mt-3xl"
      >
        <btn
          class="product-card-sawmill__add-to-cart"
          :label="$string('product.cta.shop_now')"
          :url="product.url"
          modifiers="primary center"
          :show-icon="false"
          type="button"
        />
      </div>
    </a>
    <div class="product-card-sawmill__image-container">
      <div
        v-if="product.featuredImage"
        class="product-card-sawmill__primary-image"
      >
        <splide
          ref="splide"
          :has-track="false"
          :options="config"
        >
          <splide-track>
            <splide-slide
              v-for="(image, index) of product.images"
              :key="`image-${index}`"
            >
              <a
                :href="product.url"
              >
                <responsive-image
                  :image="image"
                  min-max="300-1200"
                  ratio="3:2"
                  @image-loaded="showSecondaryImage = true"
                />
              </a>
            </splide-slide>
          </splide-track>

          <splide-arrows
            :curent-index="currentIndex"
            :page-count="product.images.length"
          />
        </splide>
      </div>
    </div>

    <div class="product-card-sawmill__specs">
      <product-stat
        v-if="product.logDiameter"
        :label="$string('compare.log_diameter')"
        :value="product.logDiameter"
        :unit="$string('compare.log_diameter_unit')"
        label-class="text-body-xs-bold text-heading-3xs-desktop"
        unit-class="text-heading-3xs"
        number-class="text-heading-l"
      />

      <product-stat
        v-if="product.widthOfCut"
        :label="$string('compare.width_of_cut')"
        :value="product.widthOfCut"
        :unit="$string('compare.width_of_cut_unit')"
        label-class="text-body-xs-bold text-heading-3xs-desktop"
        unit-class="text-heading-3xs"
        number-class="text-heading-l"
      />

      <product-stat
        v-if="product.maxHorsepower"
        :label="$string('compare.max_horsepower')"
        :value="product.maxHorsepower"
        :unit="$string('compare.max_horsepower_unit')"
        label-class="text-body-xs-bold text-heading-3xs-desktop"
        unit-class="text-heading-3xs"
        number-class="text-heading-l"
      />
    </div>

    <div
      v-if="isMobile"
      class="product-card-sawmill__mobile-action mt-xl"
    >
      <btn
        class="product-card-sawmill__add-to-cart"
        :label="$string('product.cta.shop_now')"
        :url="product.url"
        modifiers="primary center"
        :show-icon="false"
        type="button"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product card sawmill
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {Boolean|Object} product - Product object, false shows loading state.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import ProductBadge from '~async/product-badge/product-badge.vue'
import ProductStat from '~global/product-stat/product-stat'
import Btn from '~global/btn/btn'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import SplideArrows from '~global/splide-arrows/splide-arrows'

export default {
  name: 'ProductCardSawmill',

  components: {
    ProductPrices,
    ResponsiveImage,
    Splide,
    SplideSlide,
    SplideTrack,
    Btn,
    ProductStat,
    ProductBadge,
    SplideArrows,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    labels: {
      type: Object,
      default: () => ({
        new: '',
        sale: '',
        most_popular: '',
        best_seller: '',
        freeShipping: '',
      }),
    },
  },

  emits: {
    'product-card-loaded': () => true,
  },

  data() {
    return {
      currentIndex: 1,
      config: {
        arrows: true,
        pagination: false,
      },
      loadSecondaryImage: false,
      showSecondaryImage: false,
      sale: false,
      hasBadge: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),
  },

  watch: {

    /**
     * Watch isMobile to close on change.
     */
    isMobile() {
      this.getTruncateLimit(this.isMobile)
    },
  },

  mounted() {
    this.setEventListeners()
    this.getProductBadges()
    this.emitState()
     
    //let breadIntervall = setInterval(function() {
    //  if (window.BreadPayments) {
    //    this.loadBreadALAA();
    //    clearInterval(breadIntervall);
    //  }
    //}, 100);

  },

  methods: {

    loadBreadALAA() {
   
   let breadAsLowAsContainers = document.getElementsByClassName("bread-as-low-as-container");
   let breadPlacements = []
   let setup = {integrationKey: '4ad64e7a-d472-4404-bf33-e7ff260ec543'};
   for (const container of breadAsLowAsContainers) {
       let placement = {};
       let currency = "USD";
       placement.allowCheckout = false;
       placement.domID = container.id;
       placement.order = {};
       placement.order.items = [];
 
       let price = Number(container.dataset.price);
 
       placement.order.subTotal = {value: price, currency: currency};
       placement.order.totalPrice = {value: price, currency: currency};
       placement.order.currency = currency;
       placement.order.totalShipping = {value: 0, currency: currency};
       placement.order.totalTax = {value: 0, currency: currency};
       placement.order.totalDiscounts = {value: 0, currency: currency};
       breadPlacements.push(placement);
     }
     window.BreadPayments.setup(setup);
     window.BreadPayments.on('INSTALLMENT:APPLICATION_DECISIONED', () => {});
     window.BreadPayments.on('INSTALLMENT:APPLICATION_CHECKOUT', () => {});
     window.BreadPayments.registerPlacements(breadPlacements);
     window.BreadPayments.__internal__.init();
   },
    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    emitState() {
      this.$emit('product-card-loaded')
    },

    sanitizeCompatibility(values) {
      let output = ``
      let truncated = false
      const products = values.split('|')
      const limit = this.getTruncateLimit(this.isMobile)

      products.forEach((product, index) => {

        if (index < limit) {
          output += `<span class="product-card-sawmill__compatibility-product">${product}</span>`
        } else {
          // eslint-disable-next-line no-lonely-if
          if (!truncated) {
            output += `<span class="product-card-sawmill__compatibility-product">+${products.length - index}</span>`
            truncated = true
          }
        }
      })

      return output
    },
    getTruncateLimit(isMobile) {
      if (isMobile) {
        return 3
      }
      return 4
    },
    getProductBadge() {
      const { product } = this
      //const sale = product.price.amount < product.compareAtPrice.amount
      const tags = product.tags ? product.tags.find((tag) => tag.includes('badge:')) : false
      const badges = product.badge ? product.badge[0] : false
      const badge = tags || badges
      //this.sale = sale
      const sale= '';
      if (!sale && !badge) {
        return
      }

      this.badge = sale ? this.$string('collection.badge.sale') : badge.replace('badge:', '')
    },
    getProductBadges() {
      if (!this.product && !this.liquid) {
        return
      }

      const { product } = this
      const badges = product.tags?.filter((tag) => tag.includes('badge:'))
      const sale = product.compareAtPrice.amount
        ? product.price?.amount < product.compareAtPrice?.amount
        : false

      if ((!badges || badges.length <= 0) && !sale) {
        return
      }
      const badgeTitle = badges.map((badge) => badge.replace('badge:', ''))
      this.hasBadge = true
      this.badges = badgeTitle
      //this.sale = sale
    },

    /**
     * Generate badge class to get proper badge color
     * @param {String} badge - badge name.
     */
    productBadgeClass(badge) {
      const text = badge.replace('_', '-').replace(' ', '-')
      return `product-badge--${text.toLowerCase()}`
    },

    /**
     * Generate badge text label by checking if it exists as a setting
     * @param {String} badge - badge name.
     */
    productBadgeText(badge) {
      if (badge.includes('_') && this.labels[`${badge}`]) {
        return this.labels[`${badge}`]
      }

      return badge
    },

    /*
      Set splide event listeners
    */
    bindSplideEvents() {
      this.$refs.splide.splide.on('moved', (index) => {
        const slideIndex = index + 1
        this.currentIndex = slideIndex
      })

      /*
        Check if screensize is desktop.
        Set index back to one when back to mobile view
      */
      this.$refs.splide.splide.on('resize', () => {
        if (this.isMobile) {
          return
        }

        this.currentIndex = 1
      })
    },

    setEventListeners() {
      this.bindSplideEvents()
    },
  },
}
</script>

<style lang="scss">
@import './product-card-sawmill';
</style>
